import * as React from 'react'
import classnames from "classnames";

import { Select } from '../../../../../components/Select'
import { FormItemVertical } from '../../../Forms/FormItemVertical'
import { Button } from "../../../../../components/Buttons";
import { BatchAction, ActionType, VehicleBatchEditActions } from "./flux";
import { intToString, stringToInt } from "../../../../../converters/converters";
import { Timezone, Unit } from "../../../../../rest/Misc";
import { Levels } from "../../../Levels/Levels";
import { PressureLevels, TemperatureLevels } from "../../../../../rest/GroupAction";
import { assertValue } from "../../../../utils/assert";
import { SuggestList } from "../../../SuggestList/SuggestList";
import { AddTags } from "../../../AddTags";
import { groupActionsTranslationKeys } from "./groupEditNames";
import { AddGroupAction } from "./AddGroupAction";
import { RemoveTagsGroupAction, TimezoneGroupAction, PressureGroupAction, TemperatureGroupAction } from "./GroupActions";
import { Polyglot } from '../../../../../polyglot';

const styles = require('./BatchEdit.local.css');

export interface VehicleBatchEditProps
{
    polyglot: Polyglot;
    selectedAction?: ActionType;
    actionValue?: any;
    
    addedActions: BatchAction<ActionType>[];

    axleCount: number;
    timezones: Timezone[];
    unit: Unit;
    
    allTags: string[];
    availableTags: string[];
    suggestedTags: string[];

    isGroupActionValid: boolean;

    selectGroupAction: (type: ActionType) => void;
}

type Props = Partial<VehicleBatchEditActions> & VehicleBatchEditProps;
export class VehicleBatchEdit extends React.Component<Props, any>
{
    render()
    {
        const {
            polyglot,
            selectedAction, selectGroupAction,
            isGroupActionValid,
            addedActions, commitAction,
            availableTags, suggestedTags,
            allTags, actionValue, setTags,
            timezones, setTimezone,
            setAxle, setBaseline, setRedMax, setYellowMax, setYellowMin, setRedMin, unit,
        } = this.props;
        return (
            <div className={`${styles.VehicleBatchEdit} VehicleBatchEdit`}>
                <AddGroupAction
                    polyglot={polyglot}
                    isValid={true} selectedActionType={selectedAction} onGroupActionChanged={selectGroupAction} commitAction={commitAction}
                >
                    <AddTags {...{type: ActionType.AddTags}} className={styles.EditTags} options={allTags} selected={actionValue} onChange={setTags} />
                    <RemoveTagsGroupAction suggestions={suggestedTags} className={styles.EditTags} options={availableTags} selectedTags={actionValue} onChange={setTags} />
                    <TimezoneGroupAction polyglot={polyglot} selectedTimezone={actionValue} options={timezones} onChange={setTimezone}/>
                    <PressureGroupAction polyglot={polyglot} levels={actionValue} axleCount={this.props.axleCount} setAxle={setAxle} setBaseline={setBaseline} setRedMax={setRedMax} setRedMin={setRedMin} setYellowMax={setYellowMax} setYellowMin={setYellowMin} unit={unit} setDefineAlertLevels={this.props.setDefineAlertLevelsFlag} />
                    <TemperatureGroupAction polyglot={polyglot} levels={actionValue} axleCount={this.props.axleCount} setAxle={setAxle} setRedMax={setRedMax} setRedMin={setRedMin} setYellowMax={setYellowMax} setYellowMin={setYellowMin} unit={unit} />
                </AddGroupAction>
                <div>
                { this.actionsListMarkup(addedActions) }
                </div>
            </div>
        );
    }

    actionsListMarkup(actions: BatchAction<ActionType>[])
    {
        const { removeAction, polyglot } = this.props;
        return (actions && actions.length > 0)
            ? [   
                <h4 key="1" style={{textAlign: 'center'}}>{polyglot.t('vehicle.group_actions.added_actions_title')}</h4>,
                <ul key="2" className={styles.List}>
                    {
                        actions.map((item, idx) => (
                            <li key={idx}>
                                <div className={styles.Item}>
                                    { this.actionMarkup(item) }
                                    <Button leftIcon="remove" dim="xs" onClick={() => removeAction(item.id)} />
                                </div>
                            </li>
                        ))
                    }
                </ul>
            ]
            : <div className={styles.EmptyLabel}>{polyglot.t('vehicle.group_actions.no_actions_placeholder')}</div>;
    }

    actionMarkup(action: BatchAction<ActionType>)
    {
        const { polyglot } = this.props;
        let val = action.value;

        switch(action.type) {
            case ActionType.AddTags:
            case ActionType.RemoveTags:
                val = (action.value || []).join(', ');
            break;

            case ActionType.SetTimezone:
                val = (action.value as Timezone).Display;
            break;

            case ActionType.SetTemperature:
                const tLevels =  (action.value as TemperatureLevels);
                val = joinTemperatureLevels(polyglot, tLevels, action.meta as string);
            break;

            case ActionType.SetPressure:
                const pLevels = (action.value as PressureLevels);
                val = joinPressureLevels(polyglot, pLevels, action.meta as string);
            break;

            default:
                throw new Error(`there is no implementation for ${action.type}`);
        }    
            
        return <span><b>{polyglot.t(groupActionsTranslationKeys.get(action.type))}: </b>{val}</span>;
    }
}

function joinPressureLevels(polyglot: Polyglot, levels: PressureLevels, unitName: string)
{
    const values: string[] = [];
    if(levels.Axle != null)
        values.push(`${polyglot.t('levels.axles')}=${levels.Axle > 0 ? levels.Axle : polyglot.t('levels.all_axles')}`);
    if(levels.Baseline != null)
        values.push(`${polyglot.t('levels.baseline')}=${levels.Baseline} ${unitName}`);
    if(levels.RelLevelMaximumRed != null)
        values.push(`${polyglot.t('levels.red_max')}=+${levels.RelLevelMaximumRed}%`);
    if(levels.RelLevelMaximumYellow != null)
        values.push(`${polyglot.t('levels.yellow_max')}=+${levels.RelLevelMaximumYellow}%`);
    if(levels.RelLevelMinimumYellow != null)
        values.push(`${polyglot.t('levels.yellow_min')}=-${levels.RelLevelMinimumYellow}%`);
    if(levels.RelLevelMinimumRed != null)
        values.push(`${polyglot.t('levels.red_min')}=-${levels.RelLevelMinimumRed}%`);
    return values.join(', ');
}

function joinTemperatureLevels(polyglot: Polyglot, levels: TemperatureLevels, unitName: string)
{
    const values: string[] = [];
    if(levels.Axle != null)
    values.push(`${polyglot.t('levels.axles')}=${levels.Axle > 0 ? levels.Axle : polyglot.t('levels.all_axles')}`);
    if(levels.AbsLevelMaximumRed != null)
        values.push(`${polyglot.t('levels.red_max')}=${levels.AbsLevelMaximumRed} ${unitName}`);
    if(levels.AbsLevelMaximumYellow != null)
        values.push(`${polyglot.t('levels.yellow_max')}=${levels.AbsLevelMaximumYellow} ${unitName}`);
    if(levels.AbsLevelMinimumYellow != null)
        values.push(`${polyglot.t('levels.yellow_min')}=${levels.AbsLevelMinimumYellow} ${unitName}`);
    if(levels.AbsLevelMinimumRed != null)
        values.push(`${polyglot.t('levels.red_min')}=${levels.AbsLevelMinimumRed} ${unitName}`);
    return values.join(', ');
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Vehicle/BatchEdit/BatchEdit.tsx